import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { usePrayer } from '../../contexts/PrayerContext';
import { Lottie } from 'lottie-react';
import { crescentPulseLottie, starSparkleLottie, tasbihLottie } from '../../data/lottieAnimations';
import { pickSurahForTime } from '../../utils/nextPrayer';

// ── Feature SVG icons ──────────────────────────────────────────────────────
const PrayerTimesIcon = ({ size = 40, color }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="18" stroke={color} strokeWidth="1.5" opacity="0.25" />
    {/* Clock hands */}
    <line x1="20" y1="20" x2="20" y2="8" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <line x1="20" y1="20" x2="29" y2="25" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <circle cx="20" cy="20" r="2.5" fill={color} />
    {/* Prayer marks at 12, 3 (Fajr, Dhuhr, etc.) */}
    {[0, 72, 144, 216, 288].map((angle, i) => {
      const rad = (angle - 90) * Math.PI / 180;
      const x = 20 + 15 * Math.cos(rad);
      const y = 20 + 15 * Math.sin(rad);
      return <circle key={i} cx={x} cy={y} r="1.5" fill={color} opacity="0.7" />;
    })}
    {/* Crescent at top */}
    <path d="M20 4C17 4 14.5 6.5 14.5 9.5c0 3 2.5 5.5 5.5 5.5.9 0 1.8-.2 2.5-.7A5.5 5.5 0 0020 4z" fill={color} opacity="0.8" />
  </svg>
);

const QiblaCompassIcon = ({ size = 40, color }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="17" stroke={color} strokeWidth="1.5" opacity="0.3" />
    <circle cx="20" cy="20" r="11" stroke={color} strokeWidth="1" opacity="0.2" />
    {/* Compass needle pointing NE */}
    <path d="M20 20L27 13" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    <path d="M20 20L13 27" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
    {/* 8-point star at center */}
    <path d="M20 16l.8 2.2L23 19l-2.2.8L20 22l-.8-2.2L17 19l2.2-.8z" fill={color} />
    {/* Cardinal dots */}
    <circle cx="20" cy="4" r="1.5" fill={color} opacity="0.8" />
    <circle cx="20" cy="36" r="1" fill={color} opacity="0.4" />
    <circle cx="4" cy="20" r="1" fill={color} opacity="0.4" />
    <circle cx="36" cy="20" r="1" fill={color} opacity="0.4" />
  </svg>
);

const QuranBookIcon2 = ({ size = 40, color }) => (
  <svg width={size} height={size} viewBox="0 0 40 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 5Q4 2 7 2.5Q12 4 18 5L18 33Q12 31 7 32.5Q4 33.5 3 32Z" fill={color} opacity="0.2" stroke={color} strokeWidth="1.4" strokeLinejoin="round"/>
    <path d="M22 5Q28 4 33 2.5Q36 2 37 5L37 32Q36 33.5 33 32.5Q28 31 22 33Z" fill={color} opacity="0.2" stroke={color} strokeWidth="1.4" strokeLinejoin="round"/>
    <line x1="20" y1="2" x2="20" y2="34" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <path d="M18 2Q20 0.5 22 2" stroke={color} strokeWidth="1.3" fill="none" strokeLinecap="round"/>
    {/* Text lines */}
    {[0,1,2,3].map(i => (
      <React.Fragment key={i}>
        <line x1={6} y1={10+i*5} x2={17} y2={9.5+i*5} stroke={color} strokeWidth="1.2" strokeLinecap="round" opacity="0.7" />
        <line x1={23} y1={10+i*5} x2={34} y2={9.5+i*5} stroke={color} strokeWidth="1.2" strokeLinecap="round" opacity="0.7" />
      </React.Fragment>
    ))}
  </svg>
);

const TrackerIcon2 = ({ size = 40, color }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Calendar base */}
    <rect x="4" y="8" width="32" height="28" rx="4" stroke={color} strokeWidth="1.5" opacity="0.3" />
    <rect x="4" y="8" width="32" height="10" rx="4" fill={color} opacity="0.2" />
    <line x1="4" y1="18" x2="36" y2="18" stroke={color} strokeWidth="1" opacity="0.3" />
    {/* Calendar dots (days) */}
    {[[11,24],[18,24],[25,24],[11,31],[18,31],[25,31]].map(([cx,cy],i) => (
      <circle key={i} cx={cx} cy={cy} r="2.5" fill={color} opacity={i < 4 ? 0.8 : 0.3} />
    ))}
    {/* Checkmarks on first 3 */}
    {[[11,24],[18,24],[25,24]].map(([cx,cy],i) => (
      <path key={i} d={`M${cx-1.5} ${cy} l1.5 1.5 l2.5-2.5`} stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
    ))}
    {/* Flame */}
    <path d="M30 5C30 8.5 27.5 10.5 26 11.5c.5-1.5.8-3 .4-4.5.6.8 1 1.8 1.2 3C28.8 8 30 6.5 30 5z" fill={color} opacity="0.85" />
  </svg>
);

const ReadSurahIcon = ({ size = 40, color }) => (
  <svg width={size} height={size} viewBox="0 0 40 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 6Q5 3 8 3.5Q14 5 20 6L20 32Q14 30 8 31.5Q5 32.5 4 31Z" fill={color} opacity="0.2" stroke={color} strokeWidth="1.4" strokeLinejoin="round"/>
    <line x1="20" y1="6" x2="20" y2="32" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    {[0,1,2].map(i => <line key={i} x1={8} y1={12+i*5} x2={17} y2={11.5+i*5} stroke={color} strokeWidth="1.2" strokeLinecap="round" opacity="0.7"/>)}
    {/* Sound waves — "recite aloud" */}
    <path d="M26 14 Q29 18 26 22" stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round" opacity="0.85"/>
    <path d="M30 11 Q35 18 30 25" stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round" opacity="0.5"/>
  </svg>
);

const GardenIcon = ({ size = 40, color }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 36 V19" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <path d="M20 25 Q12 23 9 14 Q18 14 20 25Z" fill={color} opacity="0.3" stroke={color} strokeWidth="1.3" strokeLinejoin="round"/>
    <path d="M20 21 Q28 19 31 10 Q22 10 20 21Z" fill={color} opacity="0.3" stroke={color} strokeWidth="1.3" strokeLinejoin="round"/>
    <circle cx="20" cy="9" r="4" fill={color} opacity="0.85"/>
    <path d="M10 36 h20" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
  </svg>
);

const PlayPrayerIcon = ({ size = 40, color }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="17" stroke={color} strokeWidth="1.5" opacity="0.3"/>
    <path d="M15 13 L28 20 L15 27 Z" fill={color} opacity="0.85"/>
    <path d="M20 2C17.5 2 15.5 4 15.5 6.5A4.5 4.5 0 0020 11c.8 0 1.5-.2 2.1-.5A4.5 4.5 0 0120 2z" fill={color} opacity="0.7"/>
  </svg>
);

// Feature data
const FEATURES = [
  {
    id: 'prayer',
    titleKey: 'Prayer Times',
    descKey: 'Auto-location based times for all 5 daily prayers',
    path: '/',
    gradient: ['#1a365d', '#2d5a87'],
    textColor: '#93c5fd',
    icon: (color) => <PrayerTimesIcon size={38} color={color} />,
    lottie: crescentPulseLottie,
    arabicLabel: 'أوقات الصلاة',
  },
  {
    id: 'quran',
    titleKey: 'Quran Reader',
    descKey: '114 Surahs with audio, Arabic & translations',
    path: '/quran',
    gradient: ['#064e3b', '#047857'],
    textColor: '#6ee7b7',
    icon: (color) => <QuranBookIcon2 size={38} color={color} />,
    lottie: tasbihLottie,
    arabicLabel: 'القرآن الكريم',
  },
  {
    id: 'qibla',
    titleKey: 'Qibla Direction',
    descKey: 'Accurate compass to Makkah from anywhere',
    path: '/qibla',
    gradient: ['#581c87', '#7c3aed'],
    textColor: '#c4b5fd',
    icon: (color) => <QiblaCompassIcon size={38} color={color} />,
    lottie: starSparkleLottie,
    arabicLabel: 'القبلة',
  },
  {
    id: 'tracker',
    titleKey: 'Prayer Tracker',
    descKey: 'Streaks, stats, Qasr/Jama\' & Qaza logs',
    path: '/tracker',
    gradient: ['#7c2d12', '#c2410c'],
    textColor: '#fdba74',
    icon: (color) => <TrackerIcon2 size={38} color={color} />,
    lottie: starSparkleLottie,
    arabicLabel: 'متابعة الصلاة',
  },
  {
    id: 'readSurah',
    titleKey: 'Read me a Surah',
    descKey: 'Sit back — we\'ll recite a Surah aloud for you',
    action: (navigate, ctx) => { const n = pickSurahForTime(ctx?.prayerTimes); navigate(`/quran/surah/${n}?autoplay=1`); },
    gradient: ['#0f766e', '#14b8a6'],
    textColor: '#99f6e4',
    icon: (color) => <ReadSurahIcon size={38} color={color} />,
    lottie: tasbihLottie,
    arabicLabel: 'اقرأ لي سورة',
    cta: 'Listen',
  },
  {
    id: 'garden',
    titleKey: 'My Jannah Garden',
    descKey: 'Watch your garden grow with every verse recited',
    action: (navigate) => navigate('/quran?tab=saved'),
    gradient: ['#166534', '#16a34a'],
    textColor: '#bbf7d0',
    icon: (color) => <GardenIcon size={38} color={color} />,
    lottie: starSparkleLottie,
    arabicLabel: 'جنتي',
    cta: 'View garden',
  },
  {
    id: 'playUntilPrayer',
    titleKey: 'Play until Next Prayer',
    descKey: 'Continuous recitation that stops at the next adhan',
    action: (navigate) => {
      let chapter;
      try {
        const lr = JSON.parse(localStorage.getItem('quran_last_read') || '{}');
        const entries = Object.entries(lr);
        if (entries.length) chapter = entries.sort((a, b) => b[1].ts - a[1].ts)[0][0];
      } catch { /* ignore */ }
      if (!chapter) chapter = Math.floor(Math.random() * 114) + 1;
      navigate(`/quran/surah/${chapter}?autoplay=1&until=prayer`);
    },
    gradient: ['#1e3a8a', '#3b82f6'],
    textColor: '#bfdbfe',
    icon: (color) => <PlayPrayerIcon size={38} color={color} />,
    lottie: crescentPulseLottie,
    arabicLabel: 'اقرأ حتى الصلاة',
    cta: 'Start',
  },
];

const FeatureBanners = () => {
  const navigate = useNavigate();
  const { isDarkMode, isRTL } = useTheme();
  const { prayerTimes } = usePrayer();
  const [activeIdx, setActiveIdx] = useState(0);
  const sliderRef = useRef(null);
  const autoRef = useRef(null);

  // Auto-advance every 4 seconds
  useEffect(() => {
    autoRef.current = setInterval(() => {
      setActiveIdx(p => (p + 1) % FEATURES.length);
    }, 4000);
    return () => clearInterval(autoRef.current);
  }, []);

  // Scroll slider to active card (use container-relative scrollTo, NOT scrollIntoView)
  useEffect(() => {
    if (!sliderRef.current) return;
    const cards = sliderRef.current.children;
    const card = cards[activeIdx];
    if (!card) return;
    const slider = sliderRef.current;
    // Center the card within the slider without scrolling ancestors
    const scrollLeft = card.offsetLeft - (slider.clientWidth - card.offsetWidth) / 2;
    slider.scrollTo({ left: scrollLeft, behavior: 'smooth' });
  }, [activeIdx]);

  const pauseAuto = () => { clearInterval(autoRef.current); };
  const resumeAuto = () => {
    clearInterval(autoRef.current);
    autoRef.current = setInterval(() => setActiveIdx(p => (p + 1) % FEATURES.length), 4000);
  };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Horizontal scroll slider */}
      <div
        ref={sliderRef}
        className="flex gap-3 overflow-x-auto pb-2 relative"
        style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', scrollSnapType: 'x mandatory' }}
        onTouchStart={pauseAuto}
        onTouchEnd={resumeAuto}
        onMouseEnter={pauseAuto}
        onMouseLeave={resumeAuto}
      >
        {FEATURES.map((f, idx) => (
          <button
            key={f.id}
            data-testid={`feature-banner-${f.id}`}
            onClick={() => { pauseAuto(); setActiveIdx(idx); if (f.action) f.action(navigate, { prayerTimes }); else navigate(f.path); }}
            className={`flex-shrink-0 w-[72vw] max-w-[280px] rounded-3xl overflow-hidden relative text-left transition-all ${
              activeIdx === idx ? 'scale-100 shadow-xl' : 'scale-[0.97] opacity-80'
            }`}
            style={{
              background: `linear-gradient(135deg, ${f.gradient[0]}, ${f.gradient[1]})`,
              scrollSnapAlign: 'center',
            }}
          >
            {/* Subtle geo pattern */}
            <div className="absolute inset-0 opacity-[0.06]">
              <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                <defs><pattern id={`fp${f.id}`} x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
                  <path d="M14 2l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" fill="white"/>
                </pattern></defs>
                <rect width="100%" height="100%" fill={`url(#fp${f.id})`} />
              </svg>
            </div>

            <div className="relative z-10 p-4">
              {/* Header: icon + lottie */}
              <div className="flex items-start justify-between mb-3">
                {f.icon(f.textColor)}
                <div className="w-12 h-12 opacity-60">
                  <Lottie src={f.lottie} loop autoplay style={{ width: 48, height: 48 }} />
                </div>
              </div>

              {/* Arabic subtitle */}
              <p className="text-xs font-medium opacity-60 mb-0.5" style={{ fontFamily: "'Amiri', serif", direction: 'rtl', color: f.textColor }}>
                {f.arabicLabel}
              </p>

              {/* Title */}
              <p className="font-black text-lg text-white leading-tight mb-1">
                {f.titleKey}
              </p>

              {/* Description */}
              <p className="text-xs leading-relaxed opacity-75 text-white">
                {f.descKey}
              </p>

              {/* CTA */}
              <div className="mt-3 flex items-center gap-1">
                <span className="text-xs font-bold" style={{ color: f.textColor }}>{f.cta || 'Explore'} →</span>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="flex justify-center gap-1.5 mt-2">
        {FEATURES.map((f, i) => (
          <button
            key={i}
            data-testid={`feature-banner-dot-${i}`}
            onClick={() => { pauseAuto(); setActiveIdx(i); resumeAuto(); }}
            className={`rounded-full transition-all duration-300 ${
              activeIdx === i
                ? isDarkMode ? 'w-5 h-1.5 bg-amber-500' : 'w-5 h-1.5 bg-[var(--p-hex)]'
                : isDarkMode ? 'w-1.5 h-1.5 bg-white/30' : 'w-1.5 h-1.5 bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default FeatureBanners;
